import { APP_ROUTES } from "@/constants/app-routes";
import { QueryKeys } from "@/constants/query-keys";
import { trackOnboardingV5Completed } from "@/features/onboarding/analytics";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { onboardingContentStyle } from "@/features/onboarding/constants/theme";
import { setSmartlookOnboardingEvent } from "@/features/user-profile/utils/smartlook-onboarding";
import { useCreatePlan } from "@/hooks/requests/plan/use-create-plan";
import { useMarkOnboardCompleted } from "@/hooks/requests/user/use-mark-onboard-completed";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { triggerHaptic } from "@/utils/haptics";
import { safeAreaPaddingFloor } from "@/utils/safe-area-classes";
import { onboardingFadeTransition } from "@/utils/transitions/onboarding-fade-transition";
import { IonContent, IonPage, useIonRouter, useIonViewDidEnter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

const PREVIEW_VIDEO = "/assets/videos/app-preview.mp4";

/**
 * Final v5 onboarding screen ("Screen App Preview Video", Figma 952:1706): the
 * app preview clip fills everything above the caption, and Continue finalizes
 * onboarding. The plan itself was already generated on the Building step; here
 * we only mark completion.
 *
 * Invalidating [SUBSCRIPTION_STATUS] alongside [USER] after completion is
 * load-bearing — see CLAUDE.md ("Auth flows must invalidate...").
 */
const AppPreviewPage = () => {
  const navigation = useIonRouter();
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { markOnboardCompletedAsync, isPending } = useMarkOnboardCompleted();
  const { updatePlanAsync } = useCreatePlan();
  const { showError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // iOS blocks autoplay in Low Power Mode, and a page that entered while
  // backgrounded starts paused. Nudge it once the view is actually on screen.
  //
  // React's `muted` JSX prop only sets the DOM *property*, not the `muted`
  // HTML attribute. WebKit gates autoplay on the attribute at load time, sees
  // an un-muted autoplay, blocks it, and shows its play glyph. Force muted on
  // the element before calling play() so iOS treats it as a muted clip.
  const playVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.play().catch(() => {});
  };

  useIonViewDidEnter(() => {
    playVideo();
  });

  const handleFinalize = async () => {
    if (isPending || isSubmitting) return;
    triggerHaptic();
    setIsSubmitting(true);
    try {
      // The plan is created here — after any calorie/macro amends on the
      // confirmation screen — so it is always built from the final targets.
      await updatePlanAsync();
      await markOnboardCompletedAsync();
      trackOnboardingV5Completed();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKeys.USER] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeys.SUBSCRIPTION_STATUS] }),
      ]);
      setSmartlookOnboardingEvent({ type: "completed" });
      // Onboarding is finalized here; the Meet screen is a static welcome whose
      // CTA enters the app, so all API work stays on this screen.
      navigation.push(
        APP_ROUTES.ONBOARDING_MEET,
        "forward",
        "replace",
        undefined,
        onboardingFadeTransition,
      );
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen style={onboardingContentStyle}>
        <div className="relative flex h-full flex-col overflow-hidden bg-[#130C2D]">
          {/*
            The clip runs edge to edge under the status bar and takes whatever height
            is left above the caption — 638px of the 852px frame at design size.
            Its own backdrop is white, so the container is white too: no black flash
            before the first frame decodes.
          */}
          <div className="relative min-h-0 flex-1">
            <video
              ref={videoRef}
              src={PREVIEW_VIDEO}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              onClick={playVideo}
              className="h-full w-full object-cover"
            />
          </div>

          {/*
            Figma reserves exactly 34px under the button — the space the home
            indicator sits in. On iOS that is what the inset reports, so the
            frame is reproduced as drawn; the 34px floor keeps the button off
            the edge on devices that report no inset at all.

            Note this is the safe area itself floored at 34px, not `inset + 34px`
            — unlike the other v5 screens, which pad by `inset + 12px`
            (onboarding-step-shell-v5).
          */}
          <div
            className={cn(
              "flex shrink-0 flex-col items-center px-9 pt-[22px]",
              safeAreaPaddingFloor("34px"),
            )}
          >
            <p className="w-[272px] text-center font-[family-name:var(--font-crimson-text)] text-[30px] leading-[36px] font-semibold tracking-[-1px] text-white">
              One voice note.
              <br />
              Food tracked <span className="text-[#9454ef] italic">in seconds.</span>
            </p>

            <OnboardingPillButton
              text="Continue"
              onClick={handleFinalize}
              disabled={isPending || isSubmitting}
              className="mt-[34px]"
            />
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AppPreviewPage;
